import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Reveal } from '../shared/reveal/reveal';
import { listPosts } from '../shared/blog/blog-api';
import { formatDate } from '../shared/blog/format-date';
import { isBlogConfigured } from '../../config';
import type { PostSummary } from '../shared/blog/contracts/post';

/**
 * Home-page teaser: the latest few published posts as compact terminal rows,
 * linking into the full blog. Renders nothing if the blog API isn't configured
 * or there are no posts yet, so the home page degrades cleanly.
 */
export function BlogTeaser() {
  const [posts, setPosts] = useState<PostSummary[] | null>(null);

  useEffect(() => {
    if (!isBlogConfigured()) return;
    const controller = new AbortController();
    listPosts({ limit: 3 }, controller.signal)
      .then((res) => setPosts(res.items))
      .catch(() => setPosts([])); // fail quietly on the home page
    return () => controller.abort();
  }, []);

  if (!posts || posts.length === 0) return null;

  return (
    <section id="blog" className="mx-auto max-w-content px-5 py-16">
      <Reveal>
        <p className="section-kicker">{'// 04 — blog'}</p>
        <h2 className="mt-2 font-mono text-2xl font-bold tracking-tight text-fg sm:text-3xl">
          Latest writing
        </h2>
      </Reveal>

      <Reveal className="mt-8">
        <div className="terminal-card divide-y divide-border overflow-hidden">
          {posts.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group flex items-start gap-3 px-4 py-4 transition-colors hover:bg-elevated"
            >
              <span aria-hidden className="mt-0.5 font-mono text-sm text-accent">
                {'>'}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="font-mono text-sm font-medium text-fg">{post.title}</span>
                  {post.publishedAt && (
                    <span className="font-mono text-[11px] tabular-nums text-muted">
                      {formatDate(post.publishedAt)}
                    </span>
                  )}
                </span>
                {post.excerpt && (
                  <span className="mt-1 block line-clamp-2 text-sm leading-relaxed text-muted">
                    {post.excerpt}
                  </span>
                )}
              </span>
            </Link>
          ))}
        </div>
      </Reveal>

      <Reveal className="mt-6">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 font-mono text-sm text-accent hover:underline"
        >
          view all posts <span aria-hidden>→</span>
        </Link>
      </Reveal>
    </section>
  );
}
